import { types, flow, cast } from 'mobx-state-tree';
import getAppRoot from './utils/getAppRoot';
import resolveLastViewedDate from './utils/resolveLastViewedDate';
import { v4 as uuidv4 } from 'uuid';
import { notification } from '@routific/hive';
import { assignStopsFailedNotification, noOrdersToScheduleNotification, noRoutesToInsertNotification } from '../../notifications/scheduling/assignStops';
import { trackScheduleAll, trackSchedulingRequested } from '../../analytics/eventTrackers/scheduling';
import { AnalyticsEventsProps } from '../../analytics/analyticsEventsProps';
import { RouteDtoModelType } from './models';
import { CannotBeScheduledToKeysType } from '../../components/routes/RoutesTable';
import { Pages } from './Router/constants';
import { logglyLogger } from '../../services/logglyLogger';

const baseExclusionCriteria = {
  isBeingModified: true,
  isDeleting: true,
  deleted: true,
  finished: true,
} as CannotBeScheduledToKeysType;

const resolveTargetRouteUuids = (self: {
  selectedRouteUuidsArray: string[];
  availableRoutes: RouteDtoModelType[];
}): string[] => {
  if (self.selectedRouteUuidsArray.length > 0) {
    return self.selectedRouteUuidsArray;
  }
  return self.availableRoutes
    .map((route) => route.uuid)
    .filter((uuid): uuid is string => uuid !== undefined);
};

const getSchedulingParams = (
  self: {
    isSchedulingSelectedVisits: boolean;
    selectedVisitUuidsArray: string[];
    selectedRouteUuidsArray: string[];
    availableRoutes: RouteDtoModelType[];
    ensureSelectedRouteUuids: () => void;
  },
  unscheduledVisits: { uuid?: string }[],
): { orderUuids: string[]; routeUuids: string[] } => {
  if (self.isSchedulingSelectedVisits) {
    self.ensureSelectedRouteUuids();
    return {
      orderUuids: self.selectedVisitUuidsArray,
      routeUuids: self.selectedRouteUuidsArray,
    };
  }

  const orderUuids = unscheduledVisits
    .map((visit) => visit.uuid)
    .filter((uuid): uuid is string => !!uuid);

  return {
    orderUuids,
    routeUuids: resolveTargetRouteUuids(self),
  };
};

// Marks the action so its completion handler knows how to refresh the routes
// and the map once the backend finishes.
const trackSchedulingAction = (
  planningActions: {
    trackFetchAllRoutesAction: (actionUuid: string) => void;
    trackScaffoldRoutesAction: (actionUuid: string) => void;
    trackScheduleAllAction: (actionUuid: string) => void;
  },
  actionUuid: string | undefined,
  { reoptimize, isScaffold, isScheduleAll }: { reoptimize: boolean; isScaffold: boolean; isScheduleAll: boolean },
) => {
  if (!actionUuid) {
    return;
  }
  if (reoptimize) {
    planningActions.trackFetchAllRoutesAction(actionUuid);
  }
  if (isScaffold) {
    planningActions.trackScaffoldRoutesAction(actionUuid);
  }
  if (isScheduleAll) {
    planningActions.trackScheduleAllAction(actionUuid);
  }
};

const SchedulingWorkflow = types.model('SchedulingWorkflow')
  .props({
    selectedVisitUuids: types.maybeNull(types.array(types.string)),
    selectedRouteIds: types.maybeNull(types.array(types.string)),
    selectedRouteUuids: types.maybeNull(types.array(types.string)),
    showReoptimizeModal: types.maybeNull(types.boolean),
    previewDate: types.maybeNull(types.string),
  })
  .volatile(() => ({
    isExecuting: false,
    error: null as Error | null,
  }))
  .views((self) => {
    return {
      get isSchedulingSelectedVisits(): boolean {
        return !!self.selectedVisitUuids && self.selectedVisitUuids.length > 0;
      },

      get selectedRouteIdsArray(): string[] {
        return self.selectedRouteIds ? self.selectedRouteIds.slice() : [];
      },

      // Returns filter criteria for routes that cannot be scheduled to (for UI display)
      get cannotBeScheduledToKeys(): CannotBeScheduledToKeysType {
        return baseExclusionCriteria;
      },

      get targetStartDate(): string | undefined {
        return self.previewDate ?? getAppRoot(self).ui.selectedDate;
      },

      get availableRoutes(): RouteDtoModelType[] {
        const root = getAppRoot(self);
        return root.sortedRoutesByDay(this.targetStartDate).filter(
          (route) => !route.isBeingModified && !route.isDeleting && !route.deleted && !route.finished,
        );
      },

      get routeCount(): number {
        return self.selectedRouteUuids?.length || this.availableRoutes.length;
      },

      get selectedRouteUuidsArray(): string[] {
        return self.selectedRouteUuids ? self.selectedRouteUuids.slice() : [];
      },

      get selectedVisitUuidsArray(): string[] {
        return self.selectedVisitUuids ? self.selectedVisitUuids.slice() : [];
      },
    };
  })
  .actions((self) => {
    return {
      setIsExecuting(executing: boolean) {
        self.isExecuting = executing;
      },

      setError(error: Error | null) {
        self.error = error;
      },

      setSelectedVisitUuids(uuids: string[] | null | undefined) {
        self.selectedVisitUuids = uuids ? cast(uuids) : null;
      },

      setSelectedRouteIds(ids: string[] | null | undefined) {
        self.selectedRouteIds = ids ? cast(ids) : null;
      },

      setSelectedRouteUuids(uuids: string[] | null | undefined) {
        self.selectedRouteUuids = uuids ? cast(uuids) : null;
      },

      setShowReoptimizeModal(visible: boolean) {
        self.showReoptimizeModal = visible;
      },

      setPreviewDate(date: string | null) {
        // Drop route selection on day change — uuids belong to the previous day.
        if (date !== self.previewDate) {
          self.selectedRouteIds = null;
          self.selectedRouteUuids = null;
        }
        self.previewDate = date;
      },

      // Ensures selectedRouteUuids is populated with all available routes if empty
      ensureSelectedRouteUuids() {
        if (!self.selectedRouteUuids || self.selectedRouteUuids.length === 0) {
          const availableUuids = self.availableRoutes
            .map((route) => route.uuid)
            .filter((uuid): uuid is string => uuid !== undefined);
          self.selectedRouteUuids = cast(availableUuids);
        }
      },

      clearSelectedVisitsAndRoutes() {
        self.selectedVisitUuids = null;
        self.selectedRouteIds = null;
        self.selectedRouteUuids = null;
      },

      // Seeds previewDate before paint. Empty `uuids` is a no-op; omitted `uuids`
      // clears any prior selection so a generic open can't inherit a stale session.
      openSchedulingPreview({
        uuids,
        skipOrdersStep,
      }: {
        uuids?: string[];
        skipOrdersStep?: boolean;
      } = {}) {
        if (uuids !== undefined && uuids.length === 0) {
          return;
        }
        const root = getAppRoot(self);
        if (uuids) {
          self.selectedVisitUuids = cast(uuids);
        } else {
          self.selectedVisitUuids = null;
        }
        if (skipOrdersStep) {
          root.ui.setSkipOrdersStepInScheduling(true);
        }
        self.previewDate = root.ui.selectedDate ?? resolveLastViewedDate(root.ui.selectedWorkspace);
        root.ui.setShowSchedulePreview(true);
      },
    };
  })
  .actions((self) => {

    const shouldReoptimize = flow(function* () {
      const root = getAppRoot(self);
      const params = getSchedulingParams(self, root.unscheduledVisits);

      return yield root.model.shouldReoptimize(params.orderUuids, params.routeUuids);
    });

    const scheduleOrdersToTargetRoutes = flow(function* (reoptimize: boolean) {
      const root = getAppRoot(self);
      const { orderUuids, routeUuids } = getSchedulingParams(self, root.unscheduledVisits);

      const orderSelection = self.isSchedulingSelectedVisits
        ? AnalyticsEventsProps.Scheduling.OrderSelection.SELECTION
        : AnalyticsEventsProps.Scheduling.OrderSelection.ALL;

      // With no target routes we can only proceed on the reoptimize path, which
      // scaffolds routes from active templates on the backend (using startDate).
      // moveOrdersToRoutes has no scaffolding fallback, so bail if we get here
      if (routeUuids.length === 0 && !reoptimize) {
        notification(noRoutesToInsertNotification(uuidv4(), orderUuids.length));
        self.clearSelectedVisitsAndRoutes();
        return;
      }

      if (orderUuids.length === 0) {
        notification(noOrdersToScheduleNotification(uuidv4()));
        self.clearSelectedVisitsAndRoutes();
        return;
      }

      const orderCount = orderUuids.length;
      const routeCount = routeUuids.length;
      const availableRoutesCount = self.availableRoutes.length;

      // The preview respects optimization constraints; drags (layers, Timeline) skip them.
      const useConstraintsSettings = true;
      const startDate = self.targetStartDate ?? undefined;

      self.setError(null);

      try {
        root.ui.setIsOptimizing(true);

        const planningActionResult = reoptimize
          ? yield root.model.optimizeOrdersToMultipleTargetRoutes(orderUuids, routeUuids, useConstraintsSettings, startDate)
          : yield root.model.moveOrdersToRoutes(orderUuids, routeUuids, useConstraintsSettings);

        const actionUuid = reoptimize
          ? planningActionResult?.optimizeOrdersToMultipleTargetRoutes?.actionUuid
          : planningActionResult?.moveOrdersToRoutes?.actionUuid;

        trackSchedulingAction(root.planningActions, actionUuid, {
          reoptimize,
          isScaffold: routeCount === 0,
          isScheduleAll: orderSelection === AnalyticsEventsProps.Scheduling.OrderSelection.ALL,
        });

        // Track analytics before clearing state
        const source = root.router.currentPage === Pages.ORDERS
          ? AnalyticsEventsProps.Scheduling.Source.ORDERS
          : AnalyticsEventsProps.Scheduling.Source.ROUTES;

        trackSchedulingRequested({
          orderSelection,
          availableRoutesCount,
          reoptimize,
          source,
          routeCount,
          orderCount,
        });

        if (orderSelection === AnalyticsEventsProps.Scheduling.OrderSelection.ALL) {
          trackScheduleAll();
        }
      } catch (err: any) {
        root.ui.setIsOptimizing(false);
        notification(assignStopsFailedNotification(uuidv4(), orderUuids.length));

        const mutationName = reoptimize ? 'optimizeOrdersToMultipleTargetRoutes' : 'moveOrdersToRoutes';
        const error = new Error(`${mutationName} Failed: ${err.message}`);
        self.setError(error);
        throw error;
      } finally {
        self.clearSelectedVisitsAndRoutes();
      }
    });

    const handleSchedule = flow(function* (reoptimize: boolean) {
      const root = getAppRoot(self);

      if (self.isExecuting) {
        return;
      }
      self.setIsExecuting(true);

      if (root.ui.selectedWorkspace?.id) {
        // Capture before close clears previewDate.
        const scheduledDate = self.targetStartDate;
        const fromOrdersPage = root.router.currentPage === Pages.ORDERS;
        let scheduled = false;
        try {
          yield scheduleOrdersToTargetRoutes(reoptimize);
          scheduled = true;
        } finally {
          self.setIsExecuting(false);
          self.setShowReoptimizeModal(false);
          root.mapView.setSelectedItems({ unscheduledOrders: [] });
          root.mapView.setLassoMode(null);
          root.mapView.exitDrawMode();
          root.mapView.dragVisit(undefined);
          root.mapView.setTargetRouteUuid('');
          root.ui.setShowSchedulePreview(false);
          if (scheduled) {
            const staysOnSameDay = !fromOrdersPage && scheduledDate === root.ui.selectedDate;
            if (fromOrdersPage) {
              root.ordersPage.clearSelectedOrders();
              root.router.redirectToRoutesPage(scheduledDate);
            } else if (!staysOnSameDay) {
              // selectDate clears ?view=, which closes the timeline; only switch
              // days when the schedule actually landed on a different one.
              root.ui.selectDate(scheduledDate);
            }
            // Staying on the day on screen, the route subscriptions (and the planning
            // action's finished refetch) bring the changes in without blanking the Gantt.
            const projectId = root.ui.selectedWorkspace?.id;
            if (projectId && scheduledDate && !staysOnSameDay) {
              root.model.fetchProjectRoutes(projectId, scheduledDate).catch(() => {
                logglyLogger.log({ error: 'fetchProjectRoutes failed after scheduling', info: `project ${projectId}` });
              });
            }
          }
        }
      }
    });

    const schedulingEntrypoint = flow(function* (areAllRoutesPublished?: boolean) {
      const root = getAppRoot(self);
      let shouldReoptimizeResponse = true;

      // Ask about the day being scheduled into, not `?date=`. They diverge whenever
      // the preview's date stepper moves off the routes-page day, and always on the
      // orders page (no date param → `isOptimized` read an empty day and silently
      // skipped the whole insert-vs-reoptimize decision). ROUT-3624.
      if (root.isOptimizedByDay(self.targetStartDate)) {
        try {
          // check ratio of served/unserved
          const result = yield shouldReoptimize();
          shouldReoptimizeResponse = result?.shouldReoptimize;
          const forceReoptimize = result?.forceReoptimize;
          if (shouldReoptimizeResponse && !forceReoptimize && !areAllRoutesPublished && !root.ui.disableConfirmReoptimizeModal) {
            self.setShowReoptimizeModal(true);
            return;
          }
        } catch {
          self.setShowReoptimizeModal(shouldReoptimizeResponse);
          return;
        }
      }

      yield handleSchedule(shouldReoptimizeResponse);
    });

    return {
      handleSchedule,
      schedulingEntrypoint,
      scheduleOrdersToTargetRoutes,
    };
  });

export default SchedulingWorkflow;
